import {
  DisconnectReason, ParticipantKind, Room, RoomEvent, Track,
} from 'livekit-client';
import { debug } from '../lib/debug';
import { isApiReport } from '../lib/agent/apiResult';
import { logAgentIn, logAgentOut } from '../lib/agent/interactionLog';

/**
 * The copilot's LiveKit transport — join the room, send a turn, hear the agent,
 * play its voice, drive the microphone.
 *
 * Deliberately not a React thing. A room is one long-lived connection shared by
 * every surface that talks to the assistant, and React would tear it down and
 * rebuild it on the first unlucky remount. So the room lives here, the provider
 * subscribes to it with `onRoomEvent`, and every event carries a plain object
 * whose shape is fixed by the agent contract (`pms-agent/1`).
 *
 * WHAT ARRIVES, AND ON WHICH CHANNEL
 *
 *   data  agent_event        { reply, data, actions } — the agent's answer and
 *                            its commands, once per turn            → envelope
 *   data  lk.system          over-budget notices                    → system
 *   data  lk.transcription   the worker's own copy of the user's STT → userTranscript
 *   data  (no topic)         legacy envelopes or `{ text }` lines   → envelope | chat
 *   text  lk.transcription   the agent's speech as deltas           → agentTranscript
 *                            the user's speech, sent on their behalf → userTranscript
 *   text  lk.chat            a typed reply, or an envelope as JSON  → chat | envelope
 *   attr  lk.agent.state     initializing | idle | listening | thinking | speaking
 *   audio                    the agent's TTS, played through a hidden element
 *
 * The topic decides what a packet is. The old transport turned any JSON with a
 * `message` into a chat line, which swallowed the commands of an envelope that
 * happened to carry one; an `agent_event` is never a chat line here.
 *
 * GENERATIONS
 *
 * Every room gets a generation number and every listener bound to it checks
 * that number before emitting. Disconnecting, timing out or switching rooms
 * bumps the counter, so a room that is still winding down — or a connect that
 * lands after its timeout — can never flip the provider's state back.
 */

export const TOPIC_CHAT = 'lk.chat';
export const TOPIC_TRANSCRIPTION = 'lk.transcription';
export const TOPIC_AGENT_EVENT = 'agent_event';
export const TOPIC_SYSTEM = 'lk.system';

/**
 * How long to wait for the socket before giving up and reporting it.
 *
 * LiveKit retries internally and can sit in `connecting` well past the point a
 * reader has decided the assistant is broken, so this is about telling them so
 * rather than about the protocol.
 */
const CONNECT_TIMEOUT_MS = 15000;

/**
 * Capture and subscription defaults belong on the Room, not on `connect()`.
 * Ki_UI passed them to `connect()`, where LiveKit silently ignores them, and so
 * shipped a mic with no echo cancellation.
 */
const ROOM_OPTIONS = {
  adaptiveStream: true,
  dynacast: true,
  audioCaptureDefaults: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
};

/** Stream attributes set by LiveKit Agents on transcription streams. */
const ATTR_SEGMENT_ID = 'lk.segment_id';
const ATTR_TRANSCRIPTION_FINAL = 'lk.transcription_final';
const ATTR_TRANSCRIBED_TRACK = 'lk.transcribed_track_id';
const ATTR_AGENT_STATE = 'lk.agent.state';

const AGENT_STATES = new Set(['initializing', 'idle', 'listening', 'thinking', 'speaking']);

/** The hidden element the agent's voice plays through. */
const AUDIO_CONTAINER_ID = 'ki-agent-audio';

/** Characters of a line kept in a dev trace — enough to recognise it, never a whole report. */
const TRACE_PREVIEW = 160;

/* ── Module state ─────────────────────────────────────────────────────────── */

/** The connected room. Assigned only after `connect()` resolves. */
let room = null;
/** What `room` was joined for (`wsUrl` + room name), so a repeat connect can be a no-op. */
let roomKey = null;
/** Removes this module's listeners from `room`. */
let roomUnbind = null;
/** The connect in flight: `{ key, gen, room, unbind, promise }`. */
let pending = null;
/** Bumped whenever a room stops being the current one; stale listeners compare against it. */
let generation = 0;

let agentIdentity = null;
let agentState = null;
let micEnabled = false;
let speaking = { agent: false, user: false };
let canPlayback = true;

/** Volume applied to every agent audio track, current and future. */
let agentVolume = 1;
/** Agent audio tracks this module attached: track → { el, room }. */
const attached = new Map();

/**
 * User transcripts from data packets carry no utterance id, and a timestamp
 * changes on every partial. A counter that advances after each final gives the
 * partials and the final of one utterance the same id and the next utterance a
 * new one, which is what the provider dedupes on.
 */
let dataUtterance = 0;
let dataUtteranceOpen = false;
/** Fallback ids for streams and lines that arrive without one. */
let localSeq = 0;

const listeners = new Set();
let decoder = null;

/* ── Helpers ──────────────────────────────────────────────────────────────── */

const isCurrent = (gen) => gen === generation;

function transportError(code, message, cause) {
  const err = new Error(message);
  err.code = code;
  if (cause !== undefined) err.cause = cause;
  return err;
}

const preview = (text) => {
  const s = typeof text === 'string' ? text : '';
  return s.length > TRACE_PREVIEW ? `${s.slice(0, TRACE_PREVIEW)}…` : s;
};

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

/** The backend's answer shape: any of `reply`, `data`, `actions` makes it an envelope. */
const isEnvelope = (v) => isPlainObject(v) && (hasOwn(v, 'reply') || hasOwn(v, 'data') || hasOwn(v, 'actions'));

/** Whether text so far could still turn out to be JSON, so half of it is never shown. */
const looksLikeJson = (raw) => {
  const t = raw.trimStart();
  return t.startsWith('{') || t.startsWith('[');
};

const parseJson = (raw) => {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

/** The words in a `{ text }` / `{ message }` object, or null. */
const pickText = (v) => {
  if (!isPlainObject(v)) return null;
  if (typeof v.text === 'string') return v.text;
  if (typeof v.message === 'string') return v.message;
  return null;
};

/** Connected enough to send: a signal-only reconnect keeps the data channel up. */
const canSend = (state) => state === 'connected' || state === 'signalReconnecting';

/**
 * The room name baked into a LiveKit access token (`video.room`), read without
 * verifying anything — it only decides whether a second connect is for the
 * room we are already in. Never logged.
 */
function roomFromToken(token) {
  try {
    const part = String(token).split('.')[1];
    if (!part || typeof atob !== 'function') return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, '=')));
    const name = claims?.video?.room;
    return typeof name === 'string' && name ? name : null;
  } catch {
    return null;
  }
}

/**
 * Identity of a connection target. When the token does not decode, the token
 * itself stands in: a fresh mint then counts as a different room, which costs a
 * reconnect but can never keep us in the wrong agent's room after a handoff.
 */
const connectionKey = (wsUrl, token) => {
  const name = roomFromToken(token);
  return name ? `${wsUrl}|room:${name}` : `${wsUrl}|token:${token}`;
};

/**
 * Which remote participant is the agent.
 *
 * `isAgent` first (LiveKit Agents joins with kind AGENT), then the kind itself,
 * then the `agent-` identity prefix for bots that do not set a kind. The rooms
 * can also hold human support advisors, so "anyone who is not us" — what the old
 * transport used — is not the agent.
 */
function isAgentParticipant(p, target) {
  if (!p || typeof p.identity !== 'string' || !p.identity) return false;
  const local = target?.localParticipant;
  if (p === local || (local?.identity && p.identity === local.identity)) return false;
  if (p.isAgent) return true;
  if (p.kind === ParticipantKind.AGENT) return true;
  return p.identity.startsWith('agent-');
}

/** The agent to report: the one already reported while it is still here, else the first. */
function findAgent(target) {
  if (!target) return null;
  const current = agentIdentity ? target.remoteParticipants.get(agentIdentity) : null;
  if (current && isAgentParticipant(current, target)) return current;
  for (const p of target.remoteParticipants.values()) {
    if (isAgentParticipant(p, target)) return p;
  }
  return null;
}

const readAgentState = (p) => {
  const value = p?.attributes?.[ATTR_AGENT_STATE];
  return AGENT_STATES.has(value) ? value : null;
};

/** DisconnectReason as a short lowercase word; only `disconnectRoom` says 'client'. */
function reasonName(reason) {
  if (reason === undefined || reason === null) return 'unknown';
  /* A client-initiated close we did not ask for is LiveKit's own page-leave
     handler (pagehide / beforeunload). Reporting it as 'client' would tell the
     provider the user hung up, and it would never reconnect. */
  if (reason === DisconnectReason.CLIENT_INITIATED) return 'client_initiated';
  const name = DisconnectReason[reason];
  return typeof name === 'string' ? name.toLowerCase() : String(reason);
}

/** A getUserMedia failure as the code the mic UI understands. */
function mediaErrorCode(err) {
  switch (err?.name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return 'blocked';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return 'no_device';
    default:
      return 'error';
  }
}

/* ── Event bus ────────────────────────────────────────────────────────────── */

/**
 * Everything the room says, traced in one place and compactly: partial
 * transcripts and chat lines are the same line growing, and active-speaker
 * changes fire several times a second while anyone talks, so neither is logged.
 */
function trace(event) {
  switch (event.type) {
    case 'speaking':
      return;
    case 'userTranscript':
    case 'agentTranscript':
    case 'chat':
      if (!event.final) return;
      debug(`copilot ← ${event.type}`, { id: event.id, text: preview(event.text), source: event.source });
      /* The same line, whole, in the interaction log: a preview is enough to
         follow the room, never enough to see what the agent actually said. */
      logAgentIn(event.type, { id: event.id, text: event.text, source: event.source });
      return;
    case 'envelope': {
      const p = event.payload || {};
      debug('copilot ← envelope', {
        topic: event.topic,
        reply: preview(typeof p.reply === 'string' ? p.reply : ''),
        data: isPlainObject(p.data) ? Object.keys(p.data) : p.data ?? null,
        actions: isPlainObject(p.actions) ? Object.keys(p.actions) : p.actions ?? null,
      });
      logAgentIn('envelope', { topic: event.topic, ...p });
      return;
    }
    case 'system':
      debug('copilot ← system', event);
      logAgentIn('system', event.payload ?? event);
      return;
    default:
      debug(`copilot ← ${event.type}`, event);
  }
}

function emit(event) {
  trace(event);
  /* A copy, so a listener that subscribes or unsubscribes mid-emit neither
     skips a neighbour nor hears this event twice. */
  Array.from(listeners).forEach((fn) => {
    try {
      fn(event);
    } catch (err) {
      /* One bad subscriber must not stop the others hearing the room. */
      console.error('[copilot] listener threw', err);
    }
  });
}

/** Subscribe to room events. Returns the unsubscribe. */
export function onRoomEvent(fn) {
  if (typeof fn !== 'function') return () => {};
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/* ── Derived state, emitted only on change ────────────────────────────────── */

function reportAgent(target, force = false) {
  const agent = findAgent(target);
  const identity = agent?.identity ?? null;
  if (force || identity !== agentIdentity) {
    agentIdentity = identity;
    emit({ type: 'agent', present: Boolean(identity), identity });
  }
  const state = agent ? readAgentState(agent) : null;
  if (force || state !== agentState) {
    agentState = state;
    emit({ type: 'agentState', state });
  }
}

function reportMic(enabled) {
  if (enabled === micEnabled) return;
  micEnabled = enabled;
  emit({ type: 'mic', enabled });
}

function reportSpeaking(agent, user) {
  if (agent === speaking.agent && user === speaking.user) return;
  speaking = { agent, user };
  emit({ type: 'speaking', agent, user });
}

function reportAudio(canPlay, force = false) {
  if (!force && canPlay === canPlayback) return;
  canPlayback = canPlay;
  emit({ type: 'audio', canPlayback: canPlay });
}

/**
 * Put every piece of per-room state back to "no room" and tell the provider.
 * The status goes first so a subscriber never sees "agent left" while it still
 * believes it is connected — that would read as waiting for an agent.
 */
function resetRoomState(status) {
  if (status) emit(status);
  agentIdentity = null;
  agentState = null;
  emit({ type: 'agent', present: false, identity: null });
  emit({ type: 'agentState', state: null });
  reportMic(false);
  reportSpeaking(false, false);
  /* No room, nothing blocked: an "enable voice" prompt must not outlive it. */
  reportAudio(true);
  if (dataUtteranceOpen) {
    dataUtterance += 1;
    dataUtteranceOpen = false;
  }
}

/* ── Agent audio ──────────────────────────────────────────────────────────── */

/**
 * Nothing plays a WebRTC track on the web until it is attached to a media
 * element. The container lives on `document.body`, not inside the chat pane,
 * because the pane collapses and unmounts — and audio must survive that.
 */
function audioContainer(create) {
  if (typeof document === 'undefined' || !document.body) return null;
  let el = document.getElementById(AUDIO_CONTAINER_ID);
  if (!el && create) {
    el = document.createElement('div');
    el.id = AUDIO_CONTAINER_ID;
    el.hidden = true;
    el.setAttribute('aria-hidden', 'true');
    document.body.appendChild(el);
  }
  return el;
}

/**
 * Whether this browser ignores `volume` on media elements. iOS Safari does —
 * the property reads back as 1 whatever is written — so there a volume of 0
 * is not silence, and "voice replies off" would still talk on an iPhone.
 * Probed once; everywhere else volume alone is used, because toggling `muted`
 * on a playing element can trip autoplay rules that volume never touches.
 */
let volumeReadOnly = null;
function ignoresElementVolume() {
  if (volumeReadOnly !== null) return volumeReadOnly;
  volumeReadOnly = false;
  try {
    if (typeof document !== 'undefined') {
      const probe = document.createElement('audio');
      probe.volume = 0.5;
      volumeReadOnly = probe.volume !== 0.5;
    }
  } catch {
    volumeReadOnly = false;
  }
  return volumeReadOnly;
}

/**
 * Apply the agent's volume to one of its tracks.
 *
 * Where element volume is ignored, `muted` alone is not enough: LiveKit's own
 * `Room.startAudio()` sets `muted = false` on every attached element, and it
 * runs by itself — when the mic is first published in a room (on every
 * reconnect, since each is a new Room) and, on iOS, whenever the page becomes
 * visible again. So the track itself is disabled as well: a disabled
 * MediaStreamTrack plays silence, and `startAudio` never touches it. The only
 * LiveKit code that does is a remote mute or unmute, after which this runs
 * again (`TrackUnmuted`), and the element flag is reasserted after the paths
 * that call `startAudio`.
 */
function applyVolume(track) {
  try {
    track.setVolume(agentVolume);
  } catch (err) {
    debug('copilot · volume failed', { message: err?.message });
  }
  if (!ignoresElementVolume()) return;
  const el = attached.get(track)?.el;
  if (el) el.muted = agentVolume === 0;
  try {
    if (track.mediaStreamTrack) track.mediaStreamTrack.enabled = agentVolume > 0;
  } catch (err) {
    debug('copilot · track mute failed', { message: err?.message });
  }
}

/** Every attached agent track back to the chosen volume, after LiveKit may have unmuted them. */
function reassertAgentVolume() {
  if (!ignoresElementVolume()) return;
  attached.forEach((_entry, track) => applyVolume(track));
}

function attachAgentAudio(target, gen, track, participant) {
  if (!isCurrent(gen) || !track || track.kind !== Track.Kind.Audio) return;
  if (!isAgentParticipant(participant, target)) return;
  if (attached.has(track)) {
    applyVolume(track);
    return;
  }
  const container = audioContainer(true);
  if (!container) return;
  try {
    const el = track.attach();
    el.setAttribute('data-participant', participant.identity);
    container.appendChild(el);
    attached.set(track, { el, room: target });
    /* After attach: setVolume only reaches elements already attached. */
    applyVolume(track);
    debug('copilot · agent audio attached', { from: participant.identity });
  } catch (err) {
    console.warn('[copilot] could not attach agent audio', err?.message ?? err);
  }
}

/** Always runs, stale room or not: releasing an element is cleanup, not an event. */
function detachAgentAudio(track) {
  const entry = attached.get(track);
  if (!entry) return;
  attached.delete(track);
  try {
    track.detach(entry.el);
  } catch {
    /* The track may already be stopped; the element still has to go. */
  }
  /* Removed from the DOM so LiveKit's element pool can reuse it — it only
     recycles audio elements that have no parent. */
  entry.el.remove();
  if (attached.size === 0) audioContainer(false)?.remove();
}

function releaseAgentAudio(target) {
  Array.from(attached.entries()).forEach(([track, entry]) => {
    if (!target || entry.room === target) detachAgentAudio(track);
  });
}

/* ── Incoming text streams ────────────────────────────────────────────────── */

/**
 * One `lk.transcription` stream: the agent speaking, or the user's own speech
 * published on their behalf by the agent.
 *
 * LiveKit Agents sends the agent's words as ONE delta stream per segment,
 * closed with `lk.transcription_final: "true"` in the trailer; the reader merges
 * trailer attributes into `reader.info.attributes`, so finality is read after
 * the loop. The user's words arrive as a NEW full-text stream per interim
 * result, all sharing `lk.segment_id`, with the final flag already in the
 * header — hence the segment id, not the stream id, is the utterance id.
 */
async function readTranscription(target, gen, reader, from) {
  const info = reader?.info ?? {};
  const header = info.attributes ?? {};
  const self = target.localParticipant?.identity ?? null;
  const trackSid = header[ATTR_TRANSCRIBED_TRACK];
  const isUser = Boolean((from && self && from === self)
    || (trackSid && target.localParticipant?.trackPublications?.has(trackSid)));

  if (!isUser) {
    const sender = from ? target.remoteParticipants.get(from) : null;
    if (sender && !isAgentParticipant(sender, target)) {
      debug('copilot · ignored transcription', { from });
      return;
    }
  }

  localSeq += 1;
  const id = String(header[ATTR_SEGMENT_ID] || info.id || `transcription-${localSeq}`);
  const heldToEnd = header[ATTR_TRANSCRIPTION_FINAL] === 'true';
  let text = '';
  let emitted = null;

  const send = (final) => {
    const signature = `${final}|${text}`;
    if (signature === emitted) return;
    emitted = signature;
    emit(isUser
      ? { type: 'userTranscript', id, text, final, source: 'stream' }
      : { type: 'agentTranscript', id, text, final });
  };

  try {
    for await (const chunk of reader) {
      /* Returning ends the iteration, which cancels the stream. */
      if (!isCurrent(gen)) return;
      text += chunk;
      /* A stream already marked final is the finished line; show it once, at the end. */
      if (!heldToEnd && text.trim()) send(false);
    }
  } catch (err) {
    if (!isCurrent(gen)) return;
    /* Close the line with what arrived, so a caption is never left hanging. */
    debug('copilot · transcription stream failed', { id, message: err?.message });
  }
  if (!isCurrent(gen)) return;

  const flag = (reader?.info?.attributes ?? header)[ATTR_TRANSCRIPTION_FINAL];
  /* The agent's segment is over when its stream is. A user stream that ends
     still flagged "false" is only an interim result; one with no flag at all
     came from a sender that does not stream interims, so its end is final. */
  const final = isUser ? flag === undefined || flag === 'true' : true;
  if (!final && !text.trim()) return;
  if (!isUser && !text.trim() && emitted === null) return;
  send(final);
}

/**
 * One `lk.chat` stream from the agent.
 *
 * Two forms are in use and only the sender knows which: bare text, streamed as
 * it is typed, or JSON — an envelope, or a `{ text }` object. A payload that
 * opens like JSON is held back until it closes, so the pane never flashes
 * `{"reply":"Ass` at the reader; anything else streams in as it arrives.
 */
async function readChat(target, gen, reader, from) {
  const self = target.localParticipant?.identity ?? null;
  if (from && self && from === self) return;
  const sender = from ? target.remoteParticipants.get(from) : null;
  if (sender && !isAgentParticipant(sender, target)) {
    debug('copilot · ignored chat stream', { from });
    return;
  }

  localSeq += 1;
  const id = String(reader?.info?.id || `chat-${localSeq}`);
  let raw = '';
  let streamed = false;

  try {
    for await (const chunk of reader) {
      if (!isCurrent(gen)) return;
      raw += chunk;
      if (!looksLikeJson(raw) && raw.trim()) {
        streamed = true;
        emit({ type: 'chat', id, text: raw, final: false });
      }
    }
  } catch (err) {
    if (!isCurrent(gen)) return;
    debug('copilot · chat stream failed', { id, message: err?.message });
  }
  if (!isCurrent(gen)) return;

  if (!streamed && looksLikeJson(raw)) {
    const parsed = parseJson(raw.trim());
    if (isEnvelope(parsed)) {
      emit({ type: 'envelope', payload: parsed, topic: TOPIC_CHAT });
      return;
    }
    if (parsed !== undefined) {
      const text = pickText(parsed);
      if (text === null) {
        /* Structured, but neither an envelope nor a line — braces are not a reply. */
        debug('copilot · ignored chat JSON', { id, keys: isPlainObject(parsed) ? Object.keys(parsed) : 'array' });
        return;
      }
      if (text.trim()) emit({ type: 'chat', id, text, final: true });
      return;
    }
    /* Opened like JSON but is not — treat it as the text it is. */
  }
  if (!streamed && !raw.trim()) return;
  emit({ type: 'chat', id, text: raw, final: true });
}

/* ── Incoming data packets ────────────────────────────────────────────────── */

/**
 * One data packet. Only the agent's are acted on — the same rule the stream
 * readers apply. The rooms can hold human support advisors, whose consoles
 * publish data too, and a packet from one of them must not drive the executor
 * (an `agent_event`), end the session (`lk.system`) or speak for the user (an
 * `lk.transcription` with `role: 'user'`). A packet with no participant was
 * sent through the server API, or arrived before its sender was known, and is
 * accepted as the streams accept a stream with no sender.
 */
function handleData(target, payload, participant, topic) {
  if (participant && !isAgentParticipant(participant, target)) {
    debug('copilot · ignored data packet', { topic: topic || null, from: participant.identity ?? null, reason: 'not the agent' });
    return;
  }
  let parsed;
  try {
    decoder = decoder || new TextDecoder();
    parsed = JSON.parse(decoder.decode(payload));
  } catch {
    debug('copilot · ignored data packet', { topic: topic || null, reason: 'not JSON' });
    return;
  }
  if (!isPlainObject(parsed)) {
    debug('copilot · ignored data packet', { topic: topic || null, reason: 'not an object' });
    return;
  }

  switch (topic) {
    case TOPIC_AGENT_EVENT:
      emit({ type: 'envelope', payload: parsed, topic });
      return;
    case TOPIC_SYSTEM:
      emit({ type: 'system', payload: parsed });
      return;
    case TOPIC_TRANSCRIPTION: {
      if (parsed.role !== 'user' || typeof parsed.transcript !== 'string') {
        debug('copilot · ignored transcription packet', { role: parsed.role ?? null });
        return;
      }
      const final = parsed.is_final === true;
      const text = parsed.transcript;
      if (!final && !text.trim()) return;
      emit({ type: 'userTranscript', id: `data-${dataUtterance}`, text, final, source: 'data' });
      if (final) {
        dataUtterance += 1;
        dataUtteranceOpen = false;
      } else {
        dataUtteranceOpen = true;
      }
      return;
    }
    case undefined:
    case null:
    case '': {
      if (isEnvelope(parsed)) {
        emit({ type: 'envelope', payload: parsed, topic: '' });
        return;
      }
      const text = pickText(parsed);
      if (text && text.trim()) {
        localSeq += 1;
        emit({ type: 'chat', id: `data-chat-${localSeq}`, text, final: true });
        return;
      }
      debug('copilot · ignored data packet', { topic: null, keys: Object.keys(parsed) });
      return;
    }
    default:
      debug('copilot · ignored data packet', { topic, from: participant?.identity ?? null });
  }
}

/* ── Room wiring ──────────────────────────────────────────────────────────── */

/**
 * Attach every listener to a new room, before it connects, and return the
 * function that removes them again.
 *
 * Text stream handlers go on before `connect()` because LiveKit marks its
 * stream manager connected during the join and silently drops a stream whose
 * topic has no handler — a resumed room's greeting could land in that gap. They
 * read the local identity lazily, when a stream arrives, since it is not known
 * until the join completes.
 *
 * Presence and status wait for `room === target`: the connect path reports
 * the participants already present once the room is assigned, so an event
 * from the join window would only arrive out of order. Data, streams and audio
 * need only the generation, so nothing the agent says during the join is lost.
 */
function bindRoom(target, gen) {
  const handlers = [];
  const on = (event, fn) => {
    target.on(event, fn);
    handlers.push([event, fn]);
  };
  const isLive = () => isCurrent(gen) && room === target;

  on(RoomEvent.Disconnected, (reason) => {
    /* During the join a failure rejects `connect()`, which reports it. */
    if (!isLive()) return;
    generation += 1;
    const unbind = roomUnbind;
    room = null;
    roomKey = null;
    roomUnbind = null;
    releaseAgentAudio(target);
    resetRoomState({ type: 'status', status: 'disconnected', reason: reasonName(reason) });
    unbind?.();
  });
  on(RoomEvent.Reconnecting, () => {
    if (isLive()) emit({ type: 'status', status: 'reconnecting' });
  });
  on(RoomEvent.Reconnected, () => {
    if (!isLive()) return;
    emit({ type: 'status', status: 'connected' });
    /* A full reconnect can come back to a different set of participants. */
    reportAgent(target, true);
  });

  on(RoomEvent.ParticipantConnected, (p) => {
    if (!isLive() || !isAgentParticipant(p, target)) return;
    if (agentIdentity && agentIdentity !== p.identity && target.remoteParticipants.has(agentIdentity)) {
      debug('copilot · second agent joined', { agent: agentIdentity, joined: p.identity });
      return;
    }
    agentIdentity = p.identity;
    agentState = readAgentState(p);
    emit({ type: 'agent', present: true, identity: p.identity });
    emit({ type: 'agentState', state: agentState });
  });
  on(RoomEvent.ParticipantDisconnected, (p) => {
    /* LiveKit removes the participant from the map before this fires, so
       `findAgent` sees only who is left — a handoff's next agent, or nobody. */
    if (!isLive() || !p || p.identity !== agentIdentity) return;
    agentIdentity = null;
    reportAgent(target, true);
  });
  on(RoomEvent.ParticipantAttributesChanged, (_changed, p) => {
    if (!isLive() || !p || p.identity !== agentIdentity) return;
    const state = readAgentState(p);
    if (state === agentState) return;
    agentState = state;
    emit({ type: 'agentState', state });
  });

  on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
    if (!isCurrent(gen)) return;
    const list = Array.isArray(speakers) ? speakers : [];
    const self = target.localParticipant?.identity ?? null;
    reportSpeaking(
      list.some((s) => (agentIdentity && s?.identity === agentIdentity) || isAgentParticipant(s, target)),
      list.some((s) => s === target.localParticipant || (self && s?.identity === self)),
    );
  });
  on(RoomEvent.AudioPlaybackStatusChanged, (playing) => {
    if (!isCurrent(gen)) return;
    reportAudio(Boolean(playing));
    /* Emitted by `startAudio()`, which has just unmuted every agent element. */
    reassertAgentVolume();
  });

  on(RoomEvent.TrackSubscribed, (track, _publication, participant) => {
    attachAgentAudio(target, gen, track, participant);
  });
  on(RoomEvent.TrackUnsubscribed, (track) => {
    detachAgentAudio(track);
  });

  const isLocalMic = (publication, participant) => publication?.source === Track.Source.Microphone
    && (!participant || participant === target.localParticipant
      || participant.identity === target.localParticipant?.identity);
  on(RoomEvent.LocalTrackPublished, (publication, participant) => {
    if (isCurrent(gen) && isLocalMic(publication, participant)) reportMic(!publication.isMuted);
  });
  on(RoomEvent.LocalTrackUnpublished, (publication, participant) => {
    if (isCurrent(gen) && isLocalMic(publication, participant)) reportMic(false);
  });
  on(RoomEvent.TrackMuted, (publication, participant) => {
    if (isCurrent(gen) && participant === target.localParticipant && isLocalMic(publication, participant)) {
      reportMic(false);
    }
  });
  on(RoomEvent.TrackUnmuted, (publication, participant) => {
    if (!isCurrent(gen)) return;
    if (participant === target.localParticipant && isLocalMic(publication, participant)) {
      reportMic(true);
      return;
    }
    /* A remote unmute re-enables the track underneath (see `applyVolume`). */
    const track = publication?.track;
    if (track && attached.has(track)) applyVolume(track);
  });
  on(RoomEvent.MediaDevicesError, (error) => {
    if (!isCurrent(gen)) return;
    emit({ type: 'mediaError', code: mediaErrorCode(error), message: error?.message || String(error) });
  });

  on(RoomEvent.DataReceived, (payload, participant, _kind, topic) => {
    if (isCurrent(gen)) handleData(target, payload, participant, topic);
  });

  const streamTopics = [];
  [
    [TOPIC_CHAT, readChat],
    [TOPIC_TRANSCRIPTION, readTranscription],
  ].forEach(([topic, read]) => {
    try {
      target.registerTextStreamHandler(topic, (reader, participantInfo) => {
        if (!isCurrent(gen)) return;
        read(target, gen, reader, participantInfo?.identity ?? null).catch((err) => {
          debug('copilot · stream handler failed', { topic, message: err?.message });
        });
      });
      streamTopics.push(topic);
    } catch (err) {
      /* Only a duplicate registration throws, and this room is brand new —
         but a missing handler must not stop the other channels. */
      console.warn(`[copilot] could not register ${topic}`, err?.message ?? err);
    }
  });

  let bound = true;
  return () => {
    if (!bound) return;
    bound = false;
    handlers.forEach(([event, fn]) => target.off(event, fn));
    streamTopics.forEach((topic) => {
      try {
        target.unregisterTextStreamHandler(topic);
      } catch {
        /* Nothing to undo. */
      }
    });
  };
}

/**
 * Close a room that is no longer current. Its generation is already stale, so
 * nothing it does on the way down reaches a subscriber; this only frees the
 * socket, the mic and the audio elements.
 */
async function discardRoom(target, unbind) {
  releaseAgentAudio(target);
  try {
    /* `disconnect()` stops local tracks by default, which releases the mic. It
       also aborts a join still in progress. */
    await target.disconnect();
  } catch (err) {
    debug('copilot · disconnect failed', { message: err?.message });
  } finally {
    unbind?.();
  }
}

/* ── Public API ───────────────────────────────────────────────────────────── */

/**
 * Join the room. Idempotent: a call for the room already joined (or joining)
 * gets that room (or that attempt) back, so every surface can ask without
 * coordinating. A call for a different room leaves the old one first.
 *
 * Rejects with `err.code`:
 *   'timeout'         no connection within 15 s; the attempt is abandoned and a
 *                     room that lands later is closed without a single event
 *   'connect_failed'  LiveKit refused the join
 *   'aborted'         `disconnectRoom()` or another connect replaced this one
 *   'invalid_config'  no token or URL
 * A failed connect emits no 'disconnected' status: the rejection is the report,
 * and a second signal would make the provider schedule its retry twice.
 */
export async function connectRoom({ token, wsUrl } = {}) {
  if (!token || !wsUrl) throw transportError('invalid_config', 'LiveKit token and URL are required');
  const key = connectionKey(wsUrl, token);

  if (room && roomKey === key && room.state !== 'disconnected') return room;
  if (pending && pending.key === key) return pending.promise;

  /* A different room — a handoff to another agent, or a new session. Leave the
     old one quietly: the 'connecting' below is the status the provider needs,
     and a 'disconnected' in between would read as a dropped line. */
  if (room || pending) {
    const old = room ? { room, unbind: roomUnbind } : { room: pending.room, unbind: pending.unbind };
    generation += 1;
    room = null;
    roomKey = null;
    roomUnbind = null;
    pending = null;
    resetRoomState(null);
    discardRoom(old.room, old.unbind);
  }

  generation += 1;
  const gen = generation;
  const next = new Room(ROOM_OPTIONS);
  const attempt = { key, gen, room: next, unbind: bindRoom(next, gen), promise: null };
  pending = attempt;
  attempt.promise = runConnect(attempt, wsUrl, token);
  emit({ type: 'status', status: 'connecting' });
  debug('copilot → connecting', { url: wsUrl, room: roomFromToken(token) });
  return attempt.promise;
}

async function runConnect(attempt, wsUrl, token) {
  const { room: next, gen } = attempt;
  let timer = null;
  try {
    await Promise.race([
      next.connect(wsUrl, token, { autoSubscribe: true }),
      new Promise((_, reject) => {
        timer = setTimeout(
          () => reject(transportError('timeout', 'LiveKit did not connect in time')),
          CONNECT_TIMEOUT_MS,
        );
      }),
    ]);
  } catch (err) {
    const superseded = !isCurrent(gen);
    if (!superseded) {
      /* Stale before closing, so the late room — if the join does land — is
         torn down without a word to the provider. */
      generation += 1;
      if (pending === attempt) pending = null;
      discardRoom(next, attempt.unbind);
    }
    if (superseded) throw transportError('aborted', 'The connection attempt was cancelled', err);
    if (err?.code === 'timeout') throw err;
    throw transportError('connect_failed', err?.message || 'LiveKit connection failed', err);
  } finally {
    clearTimeout(timer);
  }

  if (!isCurrent(gen)) {
    /* Cancelled while the join finished; whoever cancelled already closed it. */
    throw transportError('aborted', 'The connection attempt was cancelled');
  }

  /* Assigned BEFORE 'connected' is emitted: a subscriber that sends the moment
     it hears the room is up must find the room it is sending into. */
  pending = null;
  room = next;
  roomKey = attempt.key;
  roomUnbind = attempt.unbind;

  emit({ type: 'status', status: 'connected' });
  if (!isCurrent(gen)) throw transportError('aborted', 'The connection attempt was cancelled');
  /* An agent already in the room when we join fires no join event. */
  reportAgent(next, true);
  reportAudio(next.canPlaybackAudio, true);
  reportMic(Boolean(next.localParticipant?.isMicrophoneEnabled));

  debug('copilot → joined', {
    room: next.name,
    as: next.localParticipant?.identity ?? null,
    agent: agentIdentity,
    inRoom: Array.from(next.remoteParticipants.values()).map((p) => p.identity),
  });
  return next;
}

/**
 * Leave the room on purpose (sign-out, unmount, a user's hang-up). Emits
 * `status: 'disconnected'` with reason 'client' exactly once per room left: the
 * room is made stale first, so LiveKit's own Disconnected event is not heard.
 * With nothing joined or joining there is nothing to leave and nothing is
 * emitted.
 */
export async function disconnectRoom() {
  const target = room || pending?.room || null;
  if (!target) return;
  const unbind = room ? roomUnbind : pending.unbind;
  generation += 1;
  room = null;
  roomKey = null;
  roomUnbind = null;
  pending = null;
  resetRoomState({ type: 'status', status: 'disconnected', reason: 'client' });
  await discardRoom(target, unbind);
}

/**
 * Send one turn to the agent on `lk.chat`.
 *
 * The frame is the envelope the worker parses: it reads `text` as the turn,
 * `screen` as the screen name, and merges `context` into the turn's metadata.
 * `[api_result]` reports travel the same way, as `text`.
 *
 * Throws with `code: 'not_connected'` when the room is not up, so the caller can
 * say so (or queue) rather than drop the turn; `code: 'send_failed'` when the
 * room is up but the send itself fails.
 */
export async function sendChat({ text, context, screen } = {}) {
  const target = room;
  const frame = { text: typeof text === 'string' ? text : String(text ?? '') };
  if (screen) frame.screen = screen;
  if (context) frame.context = context;
  /* A report and a question travel the same channel but are not the same
     event to read back: one is the reader talking, the other is this app
     answering something the agent asked for. */
  const kind = isApiReport(frame.text) ? 'report' : 'turn';
  if (!target || !canSend(target.state)) {
    logAgentOut(`${kind} not sent`, { ...frame, error: 'not_connected' });
    throw transportError('not_connected', 'Copilot is not connected');
  }
  try {
    const payload = JSON.stringify(frame);
    /* Keys, not values: the context carries the whole screen digest, which
       buries the conversation when logged. The interaction log takes the
       whole frame, which is the one place it is worth that much room. */
    debug('copilot → sent', {
      text: preview(frame.text),
      screen: screen ?? null,
      context: isPlainObject(context) ? Object.keys(context) : null,
      chars: payload.length,
    });
    await target.localParticipant.sendText(payload, { topic: TOPIC_CHAT });
    logAgentOut(kind, { ...frame, chars: payload.length });
  } catch (err) {
    logAgentOut(`${kind} not sent`, { ...frame, error: err?.message || String(err) });
    if (target !== room || !canSend(target.state)) {
      throw transportError('not_connected', 'Copilot disconnected while sending', err);
    }
    throw transportError('send_failed', err?.message || 'Could not send to the copilot', err);
  }
}

/** `sendChat` for callers that retry on their own schedule. Never throws. */
export async function trySendChat({ text, context, screen } = {}) {
  try {
    await sendChat({ text, context, screen });
    return true;
  } catch (err) {
    debug('copilot → send failed', { code: err?.code ?? 'error', message: err?.message });
    return false;
  }
}

/**
 * Turn the microphone on or off. Never throws.
 *
 * Turning it on publishes the mic with the Room's capture defaults and asks
 * the browser for permission the first time. Turning it off mutes the track
 * rather than unpublishing it, so the next tap does not prompt or renegotiate.
 * Off with no room succeeds: there is no mic to be on.
 */
export async function setMicEnabled(on) {
  const enable = Boolean(on);
  const target = room;
  if (!target) {
    return enable ? { ok: false, code: 'not_connected', message: 'Copilot is not connected' } : { ok: true };
  }
  /* Muting is local and must work mid-reconnect; publishing needs the room up. */
  if (enable && !canSend(target.state)) {
    return { ok: false, code: 'not_connected', message: 'Copilot is not connected' };
  }
  try {
    await target.localParticipant.setMicrophoneEnabled(enable);
    if (target !== room) {
      return { ok: false, code: 'not_connected', message: 'Copilot disconnected' };
    }
    /* Acquiring the mic makes LiveKit call `startAudio()`, which unmutes the
       agent's elements; put "voice replies off" back. */
    if (enable) reassertAgentVolume();
    reportMic(Boolean(target.localParticipant.isMicrophoneEnabled));
    return { ok: true };
  } catch (err) {
    if (target !== room) {
      return { ok: false, code: 'not_connected', message: 'Copilot disconnected' };
    }
    const code = mediaErrorCode(err);
    debug('copilot · mic failed', { enable, code, name: err?.name ?? null });
    return { ok: false, code, message: err?.message || String(err) };
  }
}

/**
 * Let the agent's voice play. Browsers refuse audio that starts without a user
 * gesture, so this must run inside one (a click on the mic, send, or the
 * "enable voice" banner). Resolves to whether audio can play now.
 */
export async function unlockAudio() {
  const target = room || pending?.room || null;
  if (!target) return false;
  const gen = generation;
  try {
    await target.startAudio();
  } catch (err) {
    debug('copilot · audio unlock failed', { name: err?.name ?? null, message: err?.message });
  }
  /* `startAudio()` unmutes every attached element, which on a browser where
     volume is ignored would undo "voice replies off". */
  reassertAgentVolume();
  const canPlay = Boolean(target.canPlaybackAudio);
  if (isCurrent(gen)) reportAudio(canPlay);
  return canPlay;
}

/**
 * Agent voice volume, 0..1. Kept for tracks that arrive later, so a speaker set
 * to "off" stays off across reconnects and agent handoffs.
 */
export function setAgentVolume(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return;
  agentVolume = Math.min(1, Math.max(0, n));
  attached.forEach((_entry, track) => applyVolume(track));
}

/** The transport's state right now, for subscribers that arrive late. */
export function roomSnapshot() {
  /* The vocabulary of the status events. A signal-only reconnect reads as
     connected, as it does to `sendChat`, because the data channel stays up. */
  let state = 'disconnected';
  if (pending) state = 'connecting';
  if (room && room.state !== 'disconnected') {
    state = room.state === 'reconnecting' ? 'reconnecting' : 'connected';
  }
  return {
    state,
    room: room?.name || null,
    localIdentity: room?.localParticipant?.identity || null,
    agentIdentity,
    agentState,
    canPlaybackAudio: room ? Boolean(room.canPlaybackAudio) : true,
    micEnabled: room ? Boolean(room.localParticipant?.isMicrophoneEnabled) : false,
  };
}

/**
 * Emit an event as if the room had sent it — how the dev tools drive the whole
 * provider pipeline without a backend. A no-op in production builds.
 */
export function injectRoomEvent(event) {
  if (process.env.NODE_ENV === 'production') return;
  if (!isPlainObject(event) || typeof event.type !== 'string') {
    debug('copilot · ignored injected event', { event });
    return;
  }
  emit(event);
}
